import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import type { Session } from '../sessions/types';
import { IS_SHELL } from '../shell';
import { IS_MAC_VIEWER, IS_WINDOWS_VIEWER, useClipboardShare } from '../clipboard/useClipboardShare';
import { useFileTransfer } from '../files/useFileTransfer';
import { useFilePanel } from '../files/useFilePanel';
import { useFileWindow } from '../files/useFileWindow';
import { useQuality } from '../quality/useQuality';
import { useChat } from '../chat/useChat';
import { useChatLayout } from '../chat/useChatLayout';
import { useChatWindow } from '../chat/useChatWindow';
import { useRecording } from '../recording/useRecording';
import { SessionStats } from './SessionStats';
import { SessionMoreMenu, type MoreItem } from './SessionMoreMenu';
import { ChatPanel } from './ChatPanel';
import { SaveNoticeBar } from './SaveNoticeBar';
import { hasFiles } from '../files/source';
import { listenNativeFileDrops } from '../files/native';
import { ClipboardToast } from './ClipboardControls';
import { FilePanel } from './FilePanel';
import { TransferStatus } from './TransferStatus';
import { ImeCommitTracker, keyCommitsIme } from './imeCommit';
import { useFitVideo } from './useFitVideo';
import { useDrawing } from '../draw/useDrawing';
import { handleDrawKey } from '../draw/keys';
import { DrawLayer } from './DrawLayer';
import { confirmAction } from '../ui/confirm';
import { FEATURE, featureAllowed } from '../features';

/** ms → "M:SS" 표기(카운트다운). */
function fmtRemaining(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * 화면 좌표 → 원격 영상 정규 좌표(0~1). `object-fit: contain` 레터박스를 보정하고, 영상 밖(여백)이면 null.
 * 마우스 입력과 파일 끌어다 놓기(§9.4 — 놓은 위치에 붙여넣기)가 **같은 계산**을 쓴다.
 */
function videoPoint(v: HTMLVideoElement, clientX: number, clientY: number): { nx: number; ny: number } | null {
  const r = v.getBoundingClientRect();
  const vw = v.videoWidth || 1;
  const vh = v.videoHeight || 1;
  const scale = Math.min(r.width / vw, r.height / vh);
  const offX = (r.width - vw * scale) / 2;
  const offY = (r.height - vh * scale) / 2;
  const fx = (clientX - r.left - offX) / scale;
  const fy = (clientY - r.top - offY) / scale;
  if (fx < 0 || fy < 0 || fx >= vw || fy >= vh) {
    return null;
  }
  return { nx: fx / vw, ny: fy / vh };
}

/**
 * 포커스를 잃을 때 **무조건** 해제하는 키(추적에서 놓친 고착 대비 안전망).
 *
 * 🔴 **"뗌 하나만으로 아무 일도 일어나지 않는 키" 만 넣는다**(2026-09-14 실기에서 값비싸게 배웠다).
 * 종전 목록에는 Win(91·92)·**Apps(93)**·Alt(18·164·165)가 들어 있었는데, Windows 는 이 키들의
 * **키업 단독**을 의미 있는 동작으로 해석한다:
 *   - **Apps(93) → `WM_CONTEXTMENU`** = 원격 화면에 **우클릭 메뉴가 뜬다.**
 *     사용자가 브라우저 밖을 클릭할 때마다(=blur) 원격 바탕화면에 컨텍스트 메뉴가 떴다.
 *   - Win(91·92) → **시작 메뉴**가 열린다.
 *   - Alt(18·164·165) → 창의 **메뉴 모음이 활성화**된다.
 * 즉 이 안전망이 고착을 막으려다 **누르지도 않은 입력을 원격에 만들어 내고 있었다.**
 *
 * ⇒ 여기 남는 것은 Shift/Ctrl 뿐이다. 나머지는 `pressedKeys` 추적으로만 해제한다
 *   — 우리가 keydown 을 본 키는 정확히 그 키만 떼므로 유령 입력이 생기지 않는다.
 */
const MODIFIER_VKS = [16, 17, 160, 161, 162, 163];

/**
 * 끌어내기(F5) 판정 — 버튼을 누른 채 **뷰어 창 밖**에 이만큼(ms) 머물러야 "내 PC 로 꺼내기" 로 친다(2026-10-20 사용자 결정 0.3초).
 * 창 가장자리를 스치고 돌아오는 원격 안의 끌기를 거른다. 마우스 이벤트 루프 안의 onWinMove 주석 참조.
 */
const DRAGOUT_DWELL_MS = 300;

/** 통합앱: 원격 화면에서 왼쪽 버튼을 누른 동안 OS 마우스 상태를 묻는 주기(ms) — `pointerState` 주석. */
const POINTER_POLL_MS = 100;

/**
 * 통합앱: OS 가 보는 지금 마우스 상태(커서가 이 창 밖인가 · 주 버튼이 눌렸나) — 🔴 Rust `pointer_state` 명령.
 * macOS WKWebView 는 누른 채 창 밖으로 나간 마우스의 이동·뗌을 웹에 주지 않는다(2026-10-23 Mac 실기 — 끌어내기가 안 됐다).
 * 실패(옛 앱·Linux = 지원 안 함)면 reject — 호출측은 웹 판정만 쓴다.
 */
function pointerState(): Promise<{ outside: boolean; pressed: boolean }> {
  return import('@tauri-apps/api/core').then(({ invoke }) => invoke<{ outside: boolean; pressed: boolean }>('pointer_state'));
}

/**
 * Mac 뷰어에서 **원격으로 보내지 않는 키**(`KeyboardEvent.code`) — 내 Mac 의 입력 소스(한/영) 전환에 쓰는 키.
 *
 * 🔴 오른쪽 Option(`AltRight`)으로 한/영을 바꾸면 그 키가 원격 Windows 에 **Alt 단독 누름·뗌**으로 갔다
 *    (2026-09-26 Safari 진단 기록). Windows 는 Alt 단독을 **창 메뉴 모음 활성화**로 해석해,
 *    바로 뒤에 친 글자가 메뉴 단축키로 빨려 들어갈 수 있다(위 `MODIFIER_VKS` 의 Alt 사고와 같은 성질).
 * ⇒ Mac 에서는 한/영 전환이 **로컬 입력기 일**이다 — 한글은 확정 글자(`{t:u}`)로 가므로 원격의 한/영 상태와 무관하다.
 *    왼쪽 Option 은 그대로 원격 Alt 로 간다(Alt+Tab 등 조합키용).
 * 다른 전환키(Caps Lock·Ctrl+Space 등)를 막아야 하면 여기에 `code` 를 더한다.
 */
const MAC_LOCAL_ONLY_CODES: ReadonlySet<string> = new Set(['AltRight']);

/**
 * 원격 세션 뷰(전체화면 오버레이). 수신 스트림을 <video> 로 렌더하고, 영상 위 마우스/휠 입력과
 * 키보드/IME 입력을 에이전트에 전송한다(input DataChannel).
 *
 * 입력 규약(InputInjector 와 동일): 마우스 {t:m,nx,ny}/{t:b,btn,down} · 휠 {t:w,d} ·
 * 키 {t:k,vk,down}(Windows VK) · IME 조합결과 {t:u,s}(비-ASCII 만 — ASCII 는 VK 경로가 처리, 중복 방지).
 * 키보드 캡처는 숨김 <input> 에 포커스를 두어 IME 조합을 받는다(video 는 편집 불가라 조합 이벤트가 없음).
 * 조합 결과는 두 경로로 온다 — Chromium 은 `compositionend`, macOS WebKit(Mac 앱)은 조합 이벤트 없이
 * `input` 의 글자 교체(`ImeCommitTracker`). 어느 쪽이든 {t:u,s} 로 보낸다.
 */
export function SessionView({
  session,
  onDisconnect,
  active = true,
  embedded = false,
  label = '세션',
}: {
  session: Session;
  onDisconnect: () => void;
  /** 탭 여러 개 중 지금 보이는 탭인지 — 활성 탭만 키보드 포커스를 가진다(비활성이 되면 눌린 키를 모두 뗀다). */
  active?: boolean;
  /** 탭 화면 안에 들어가는지(true) / 화면 전체를 덮는지(false). */
  embedded?: boolean;
  /** 이 세션의 대상 이름(탭 제목과 같다) — 녹화 파일 이름에 쓴다(§9.7.4). */
  label?: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const imeRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  // 현재 눌려 있는 키(vk) 집합 — 포커스 상실·종료 시 일괄 keyup 으로 원격 키 고착 방지.
  const pressedKeys = useRef<Set<number>>(new Set());
  // 현재 눌려 있는 마우스 버튼 — 키와 **같은 이유**로 추적한다(고착 방지). 우리가 누른 것만 뗀다.
  const pressedButtons = useRef<Set<string>>(new Set());
  // macOS WebKit 한글 입력(조합 이벤트 없음)에서 확정 글자를 골라내는 추적기 — `imeCommit.ts` 참고.
  const imeCommit = useRef(new ImeCommitTracker());
  // 파일 전송(§9.4) — 붙여넣기·끌어다 놓기·Ctrl+C 의 "파일이면" 경로. 단축키 분기는 클립보드 훅이 함께 한다.
  const files = useFileTransfer(session);
  // 파일 패널(§9.4) — 내 PC·원격 폴더를 나란히 열어 주고받는다. 전송 자체는 위 훅이 맡는다(진행·취소가 한 자리).
  const panel = useFilePanel(session, files);
  // 파일 화면은 **별도 창**으로 뜬다(2026-09-29 사용자 결정 — 원격 화면 아래 절반을 덮지 않게). 상태는 위 `panel` 에 그대로.
  // 창을 못 띄우면(브라우저 팝업 차단) 원격 화면 안에 대신 보인다(`fileWindow.inStage`).
  const fileWindow = useFileWindow(panel, `파일 — ${label}`);
  // 화면 품질·FPS(§9.5) — 수동 선택·기본값 저장·자동 조절이 모두 이 훅 안에 있다.
  const quality = useQuality(session);
  // 채팅(§9.6) — 대화 목록·미읽음·보내기·이미지 붙여넣기가 이 훅 안에 있다.
  // 🔴 보기 전용 세션에도 쓸 수 있다(채팅은 조작이 아니라 말 걸기).
  const chatLayout = useChatLayout();
  // 닫아 둔 채 상대의 글이 오면 **저절로 연다**(2026-09-29) — 단 브라우저의 별도 창 모드는 `window.open` 이 포커스를
  // 빼앗아 원격 화면에 치던 글자가 새므로 종전처럼 배지만(앱은 창을 활성화 없이 띄울 수 있다 — `open_chat_window` quiet).
  const chat = useChat(session, IS_SHELL || chatLayout.mode !== 'popup');
  // 채팅 별도 창(§9.6.6) — 표시 방식이 `popup` 이면 대화 창을 독립 OS 창으로 띄운다(상태는 여기 `chat` 에 그대로).
  const chatWindow = useChatWindow(
    chat,
    chatLayout.mode,
    chatLayout.setMode,
    `채팅 — ${label}`,
    session.chatCaps?.maxImage ?? 0,
  );
  // 세션 녹화(§9.7) — 수신 스트림을 그대로 담는다(재인코딩 없음).
  // 🔴 상대 기기에 고지가 **표시된 것을 확인한 뒤에야** 시작한다(§9.7.1) — 훅 안에서 그 순서를 지킨다.
  // 기능 권한(§14.19.2) `record` 가 없으면 고지 통로를 주지 않는다 → 녹화 단추가 숨는다(새 에이전트는 qcap.rec=false 로도 숨긴다).
  const recording = useRecording(
    session.stream,
    featureAllowed(session.features, FEATURE.RECORD) ? (session.client.quality ?? null) : null,
    label,
  );
  // 기능 권한(§14.19.2) — 그리기 단추 표시 · 제어 권한 없음 표지.
  const canDraw = featureAllowed(session.features, FEATURE.DRAW);
  const noControlGrant = !featureAllowed(session.features, FEATURE.CONTROL);
  // 화면 그리기(§9.8) — 원격 화면 위에 펜·도형·글자. 🔴 보기 전용 세션에서도 쓴다(조작이 아니라 표시).
  // 그리기 도구를 잡은 동안(`capturesInput`)은 마우스·키가 그림으로 가고 원격으로 가지 않는다.
  const drawing = useDrawing(session, label);
  // 한 번 만든 콜백(flushIme)이 최신 값을 보게 한다.
  const drawCapturesRef = useRef(false);
  drawCapturesRef.current = drawing.capturesInput;
  // 네이티브 이벤트 콜백(한 번 등록)이 최신 훅 값을 보게 한다.
  const filesRef = useRef(files);
  filesRef.current = files;

  // ── 이벤트 기록(§9.7.5) — 녹화 중에만 쌓인다(훅이 알아서 버린다) ──────────────────
  // 🔴 **무슨 일이 있었는지만** 적는다. 채팅 본문·클립보드 값·파일 내용은 넣지 않는다.
  const note = recording.note;
  const appliedKey = quality.applied ? `${quality.applied.preset}/${quality.applied.fps}` : '';
  useEffect(() => {
    if (appliedKey) {
      note('quality', `화질 ${appliedKey}`);
    }
  }, [appliedKey, note]);
  const chatCount = chat.entries.length;
  const prevChat = useRef(chatCount);
  useEffect(() => {
    if (chatCount > prevChat.current) {
      note('chat', `대화 ${chatCount - prevChat.current}건`);
    }
    prevChat.current = chatCount;
  }, [chatCount, note]);
  const drawActive = drawing.active;
  useEffect(() => {
    note('draw', drawActive ? '그리기 켜짐' : '그리기 꺼짐');
  }, [drawActive, note]);
  const controlAllowed = session.controlAllowed;
  useEffect(() => {
    if (controlAllowed !== undefined) {
      note('control', controlAllowed ? '원격 제어 허용됨' : '보기 전용으로 바뀜');
    }
  }, [controlAllowed, note]);
  // 원격 화면 무대(끌어다 놓기 판정 — 앱은 좌표만 오므로 무대 위인지 직접 잰다).
  const stageRef = useRef<HTMLDivElement>(null);
  // 원격 화면을 무대에 비율 유지로 꽉 차게(창을 키우면 원래 해상도를 넘어서도 같이 커진다) — `useFitVideo.ts`.
  useFitVideo(stageRef, videoRef);
  // 끌어다 놓는 중(무대 테두리·안내 표시).
  const [dragging, setDragging] = useState(false);
  // 클립보드 공유(§9.3.1) — 단축키·버튼 로직은 훅에 있고, 이 화면은 키 상태에 닿는 창구만 준다.
  const clip = useClipboardShare(session, {
    isHeld: (vk) => pressedKeys.current.has(vk),
    sendRaw: (vk, down) => session.client.sendInput({ t: 'k', vk, down }),
    forward: (vk, down) => {
      if (down) {
        pressedKeys.current.add(vk);
      } else {
        pressedKeys.current.delete(vk);
      }
      session.client.sendInput({ t: 'k', vk, down });
    },
  }, files);
  // 다중 모니터: 현재 선택 모니터 인덱스(낙관적 UI — 에이전트는 전환 후 재푸시하지 않으므로 클릭 시 즉시 반영).
  const [selMon, setSelMon] = useState<number | undefined>(session.currentMonitor);
  // 목록 도착/현재값 변경 시 로컬 선택 동기화.
  useEffect(() => setSelMon(session.currentMonitor), [session.currentMonitor]);
  // 익명 세션 남은 시간(ms) — deadline 이 있을 때만 1초 간격 갱신(카운트다운).
  const [remaining, setRemaining] = useState<number | null>(null);
  useEffect(() => {
    if (!session.deadline) {
      setRemaining(null);
      return;
    }
    const tick = () => setRemaining(session.deadline! - Date.now());
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [session.deadline]);

  /** 눌린 마우스 버튼에 buttonup 전송(고착 해제). 키 해제와 같은 자리에서 함께 부른다. */
  const releaseAllButtons = useCallback(() => {
    const c = session.client;
    for (const b of pressedButtons.current) {
      c.sendInput({ t: 'b', btn: b, down: false });
    }
    pressedButtons.current.clear();
  }, [session.client]);

  /** 눌린 키 전체 + 표준 수정키에 keyup 전송(고착 해제). 포커스 상실·세션 종료·언마운트 시 호출. */
  const releaseAllKeys = useCallback(() => {
    const c = session.client;
    for (const vk of pressedKeys.current) {
      c.sendInput({ t: 'k', vk, down: false });
    }
    pressedKeys.current.clear();
    // 안전망: 추적에서 누락됐을 수 있는 표준 수정키를 강제 해제.
    for (const vk of MODIFIER_VKS) {
      c.sendInput({ t: 'k', vk, down: false });
    }
  }, [session.client]);

  /**
   * 입력기가 붙잡고 있던 마지막 한글(Mac WebKit 경로)을 확정해 보내고 입력칸을 비운다.
   * 조합과 무관한 키 누름·조합 시작·입력칸 포커스 상실 때 부른다(`imeCommit.ts`).
   */
  const flushIme = useCallback(() => {
    const el = imeRef.current;
    if (!el) {
      return;
    }
    const s = imeCommit.current.flush(el.value);
    if (s === null) {
      return; // Chromium 조합 중 — `compositionend` 가 마무리한다
    }
    if (s && !drawCapturesRef.current) {
      session.client.sendInput({ t: 'u', s }); // 그리기 중에 쌓인 조합 글자는 원격으로 보내지 않는다(§9.8.1)
    }
    el.value = '';
  }, [session.client]);

  // 전체화면 토글(원격 화면에 몰입). requestFullscreen 후 키보드 포커스 유지.
  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void rootRef.current?.requestFullscreen().then(() => imeRef.current?.focus());
    }
  };

  // 스트림 바인딩.
  useEffect(() => {
    const v = videoRef.current;
    if (v) {
      v.srcObject = session.stream;
    }
  }, [session.stream]);

  // 활성 탭이 되면 숨김 input 에 포커스(키보드 캡처), 비활성이 되면 눌린 키를 모두 뗀다.
  // ⚠️ 탭 전환은 창 blur 가 아니라 아래 blur 안전망이 걸리지 않는다 — 여기서 떼지 않으면 Ctrl 을 누른 채 탭을 바꿨을 때
  //    **원격 쪽에 Ctrl 이 눌린 채 남는다**. 여러 탭이 한 문서에 있으니 마지막에 마운트된 탭이 포커스를 뺏는 것도 막는다.
  useEffect(() => {
    if (active) {
      imeRef.current?.focus();
    } else {
      releaseAllKeys();
      releaseAllButtons();
      clip.reset();
    }
  }, [active, releaseAllKeys, releaseAllButtons, clip.reset]);

  // 그리기가 입력을 가져가는 순간 원격에 눌려 있던 키·버튼을 뗀다 — Ctrl 을 누른 채 그리기로 넘어가면
  // 그 뒤 키는 원격으로 가지 않으므로(뗌 포함) **원격에 Ctrl 이 눌린 채 남는다**(탭 전환과 같은 함정).
  const drawCaptures = drawing.capturesInput;
  useEffect(() => {
    if (drawCaptures) {
      releaseAllKeys();
      releaseAllButtons();
      clip.reset();
    }
  }, [drawCaptures, releaseAllKeys, releaseAllButtons, clip.reset]);

  // 마우스/휠 입력 캡처. object-fit: contain 레터박스를 보정해 정규화 좌표(0~1)로 변환.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) {
      return;
    }
    const client = session.client;

    const norm = (ev: MouseEvent) => videoPoint(v, ev.clientX, ev.clientY);
    const btn = (e: MouseEvent) => (e.button === 2 ? 'right' : e.button === 1 ? 'middle' : 'left');

    let last = 0;
    // 이번 누름에서 끌어내기를 이미 시작했는가 — 창 밖에서는 이동 이벤트가 연달아 오므로 한 번만 보낸다(누를 때 초기화).
    let dragOutSent = false;
    // 커서가 창 밖에 머문 시간을 재는 타이머(끌어내기 판정 — onWinMove 주석). 창 안으로 돌아오거나 버튼을 떼면 취소.
    let outsideTimer: number | undefined;
    const clearOutside = () => {
      window.clearTimeout(outsideTimer);
      outsideTimer = undefined;
    };
    /**
     * 통합앱: 왼쪽 버튼을 누른 동안 OS 에 마우스 상태를 묻는다(`pointerState` — Mac 은 웹 이벤트가 오지 않는다).
     *  - 창 밖 + 눌림이 `DRAGOUT_DWELL_MS` 이어지면 끌어내기(웹 판정 `onWinMove` 와 같은 조건 — 먼저 된 쪽 한 번만, `dragOutSent`).
     *  - OS 는 뗐는데 우리 기록엔 눌림이면 **뗌을 대신 보낸다**(창 밖에서 놓은 뗌이 웹뷰에 안 오면 원격에 버튼이 눌린 채 남는다).
     */
    let pointerTimer: number | undefined;
    let osOutsideSince = 0;
    const stopPointerWatch = () => {
      window.clearInterval(pointerTimer);
      pointerTimer = undefined;
      osOutsideSince = 0;
    };
    const startPointerWatch = () => {
      stopPointerWatch();
      pointerTimer = window.setInterval(() => {
        void pointerState().then(
          (s) => {
            if (!pressedButtons.current.has('left')) {
              stopPointerWatch(); // 웹에서 뗌을 이미 받았다
              return;
            }
            if (!s.pressed) {
              stopPointerWatch();
              clearOutside();
              pressedButtons.current.delete('left');
              client.sendInput({ t: 'b', btn: 'left', down: false });
              return;
            }
            const f = filesRef.current;
            if (dragOutSent || !f.canDragOut || !f.dragOutOn || !s.outside) {
              osOutsideSince = 0;
              return;
            }
            const now = performance.now();
            if (!osOutsideSince) {
              osOutsideSince = now;
            } else if (now - osOutsideSince >= DRAGOUT_DWELL_MS) {
              dragOutSent = true; // 이후로는 뗌 감시만 계속한다
              f.dragOut(0, 0);
            }
          },
          () => stopPointerWatch(), // 지원 안 함(옛 앱·Linux) — 웹 판정만
        );
      }, POINTER_POLL_MS);
    };
    const onMove = (e: MouseEvent) => {
      const n = norm(e);
      const now = performance.now();
      if (n && now - last > 16) {
        last = now;
        client.sendInput({ t: 'm', nx: n.nx, ny: n.ny });
      }
    };
    /**
     * 🔴 **원격 화면 밖에서 누른 버튼은 보내지 않는다**(2026-09-14 실기).
     *
     * video 요소는 화면비를 유지하느라 실제 영상보다 크고, 그 **레터박스 여백도 이 요소 안**이다.
     * 종전에는 좌표가 영상 밖이면 이동만 건너뛰고 **버튼은 그대로 보냈다** → 여백을 클릭하면
     * 원격 커서가 있던 자리에 클릭이 들어가 엉뚱한 메뉴가 떴다(사용자가 원격 PC 에서 컨텍스트 메뉴를 봤다).
     * 화면 밖은 "누르지 않은 것" 이 맞다 — 여기서 무엇을 눌렀는지 우리는 알 수 없다.
     *
     * ⚠️ 단 **뗌은 반대**다. 누른 것은 반드시 떼 줘야 한다 — 영상 안에서 누른 채 여백으로 끌고 가
     * 놓으면, 뗌을 버릴 경우 **원격에 버튼이 눌린 채 남는다**(끌기가 영영 안 끝난다).
     * ⇒ **누른 버튼을 기억했다가, 그 버튼의 뗌만 위치와 무관하게 보낸다.**
     */
    const onDown = (e: MouseEvent) => {
      imeRef.current?.focus(); // 클릭 시 키보드 포커스 유지
      const n = norm(e);
      if (!n) {
        return;
      }
      client.sendInput({ t: 'm', nx: n.nx, ny: n.ny });
      const b = btn(e);
      pressedButtons.current.add(b);
      if (b === 'left') {
        dragOutSent = false;
        if (IS_SHELL) {
          startPointerWatch();
        }
      }
      client.sendInput({ t: 'b', btn: b, down: true });
    };
    const onUp = (e: MouseEvent) => {
      const b = btn(e);
      if (b === 'left') {
        clearOutside();
        stopPointerWatch();
      }
      if (!pressedButtons.current.delete(b)) {
        return; // 우리가 누른 적 없는 버튼 — 보낼 뗌이 없다
      }
      client.sendInput({ t: 'b', btn: b, down: false });
    };
    /**
     * 끌어내기(F5, 통합앱) — **왼쪽 버튼을 누른 채 뷰어 창 밖으로 나가 잠시 머물면** 원격에서 끌던 파일을 내 PC 로 가져온다.
     *
     * 판정: ① 우리가 누른 왼쪽 버튼이 아직 눌려 있고(`pressedButtons` — 영상 안에서 시작한 끌기다)
     * ② 지금 이 순간에도 눌려 있으며(`e.buttons`) ③ 커서가 **뷰어 창 밖**에 ④ `DRAGOUT_DWELL_MS` 동안 머물렀다.
     * ⑤ [⋯] 메뉴에서 끌어내기를 꺼 두지 않았다(`dragOutOn`).
     *
     * 🔴 종전 ③ 은 "무대(원격 화면 영역) 사각형 밖" 이었다(2026-10-20 사용자 보고로 바꿈). 원격 안에서 끄는 것과
     *    내 PC 로 꺼내려는 것을 가를 확실한 단서는 **위치뿐**이다 — 끄는 내용은 둘 다 파일이고(VS Code 탐색기 끌기도 파일),
     *    같이 누르는 키는 원격 앱이 쓴다(VS Code 채팅창 첨부 = Shift+끌기). 그런데 무대 경계는 원격 화면 끝과 같아서,
     *    화면 오른쪽 끝의 VS Code 채팅창으로 끌다가 살짝만 넘어가도 **원격 끌기가 Esc 로 취소**됐다(전면이 탐색기가 아니면
     *    알림도 없이 — 사용자에게는 "Shift+끌기가 그냥 안 된다" 로 보였다). 창 안은 원격 화면이거나 우리 도구 막대라
     *    꺼내기 목적일 수 없다 ⇒ **창 밖**만 꺼내기로 친다. 머묾(④)은 창 가장자리를 스치고 돌아오는 것을 거른다.
     *    ⚠️ 대가: 전체 화면 + 모니터 하나면 창 밖이 없어 끌어내기를 쓸 수 없다(Ctrl+C → [내 PC로 받기] 로 대신한다).
     *    ⚠️ 창 밖으로 휙 나가 머묾 전에 놓으면 원격 쪽 끌기가 마지막 영상 위치에서 끝난다(종전 무대 경계에서 놓은 것과 같다).
     *
     * 🔴 **`mouseout` 으로는 잡히지 않는다**(2026-09-17 원격 x64 실기 — 사람이 끌어도 아무 일이 없었다). 기록해 보니:
     *    - 영상 가장자리를 넘을 때 오는 `mouseout` 은 옮겨 간 곳이 **무대 여백**(relatedTarget=stage)이라 "무대 안" 으로 걸러졌고,
     *    - 그 뒤 **창 밖으로** 나가는 동안 WebView2 는 마우스를 붙잡아 음수 좌표의 `mousemove`(buttons=1)만 보내며
     *      `mouseout` 은 **버튼을 뗀 뒤에야** 온다.
     *    ⇒ 창(window) 의 이동을 본다 — 누르는 동안은 창 밖 좌표(음수·창 크기 이상)도 window 로 온다.
     */
    const onWinMove = (e: MouseEvent) => {
      const f = filesRef.current;
      if (dragOutSent || !f.canDragOut || !f.dragOutOn || !pressedButtons.current.has('left') || !(e.buttons & 1)) {
        clearOutside();
        return;
      }
      const outside = e.clientX < 0 || e.clientY < 0 || e.clientX >= window.innerWidth || e.clientY >= window.innerHeight;
      if (!outside) {
        clearOutside(); // 창 안(원격 화면·도구 막대) — 원격 안의 끌기다
        return;
      }
      if (outsideTimer !== undefined) {
        return; // 이미 재는 중
      }
      outsideTimer = window.setTimeout(() => {
        outsideTimer = undefined;
        if (dragOutSent || !pressedButtons.current.has('left')) {
          return; // 그 사이 놓았다
        }
        dragOutSent = true;
        // 창 밖이라 영상 좌표가 없다 — 규약상 0 으로 보낸다(에이전트는 쓰지 않는다).
        filesRef.current.dragOut(0, 0);
      }, DRAGOUT_DWELL_MS);
    };
    const onCtx = (e: Event) => e.preventDefault();
    const onWheel = (e: WheelEvent) => client.sendInput({ t: 'w', d: -e.deltaY });

    v.addEventListener('mousemove', onMove);
    v.addEventListener('mousedown', onDown);
    // ⚠️ 뗌만 **window** 에서 듣는다. 영상 안에서 누른 채 도구막대·창 밖으로 끌고 가 놓으면
    //    video 요소에는 mouseup 이 오지 않아, 원격에 버튼이 눌린 채 남는다.
    window.addEventListener('mouseup', onUp);
    v.addEventListener('contextmenu', onCtx);
    v.addEventListener('wheel', onWheel, { passive: true });
    // 끌어내기 감지 — 누른 채 창 밖으로(누르는 동안은 창 밖 이동도 window 로 온다).
    window.addEventListener('mousemove', onWinMove);
    return () => {
      clearOutside();
      stopPointerWatch();
      v.removeEventListener('mousemove', onMove);
      v.removeEventListener('mousedown', onDown);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('mousemove', onWinMove);
      v.removeEventListener('contextmenu', onCtx);
      v.removeEventListener('wheel', onWheel);
    };
  }, [session.client]);

  // 키/키업 → {t:k,vk,down}. IME 조합 중(isComposing 또는 keyCode 229)이면 건너뛰어 조합을 방해하지 않는다.
  const onKey = (e: React.KeyboardEvent, down: boolean) => {
    // 그리기가 입력을 가져간 동안(§9.8.1) — 키는 그리기 단축키로만 쓰고 **원격으로 보내지 않는다**
    // (Ctrl+Z 가 원격 앱의 되돌리기까지 부르면 안 된다). 조합 중(한글 모드의 229)도 원격으로 보내지 않는다.
    if (drawing.capturesInput) {
      const v = videoRef.current;
      if (down) {
        handleDrawKey(e.nativeEvent, drawing.editor, v ? { w: v.clientWidth, h: v.clientHeight } : null);
      }
      e.preventDefault();
      if (imeRef.current) {
        imeRef.current.value = ''; // 한글 모드로 친 도구 단축키가 입력칸에 쌓이지 않게
      }
      return;
    }
    if (e.nativeEvent.isComposing || e.keyCode === 229) {
      return;
    }
    // 내 Mac 의 한/영 전환키 — 원격에 보내지 않는다(MAC_LOCAL_ONLY_CODES 참고). 기본 동작은 두어 입력기가 전환하게 한다.
    if (IS_MAC_VIEWER && MAC_LOCAL_ONLY_CODES.has(e.code)) {
      return;
    }
    // 조합과 무관한 키가 눌렸다 = 입력기가 붙잡고 있던 마지막 한글이 확정됐다(Mac WebKit 경로).
    // 🔴 그 키보다 **먼저** 보내야 순서가 맞다("가나다라" + Enter). 누름에서만 — Mac 은 한글 키의
    //    **뗌**을 조합 중에도 실제 keyCode 로 내보내므로(예: ㄱ 뗌 = 82) 뗌에서 확정하면 조합이 끊긴다.
    //    Shift 등 수정키 단독 누름도 확정하지 않는다 — 쌍자음 받침(있·했)이 깨진다(`keyCommitsIme`).
    if (down && keyCommitsIme(e.key)) {
      flushIme();
    }
    const vk = e.keyCode || e.which;
    // 클립보드 단축키(§9.3.1)가 먼저 본다 — 처리했으면 원격에 보내지 않는다(기본 동작을 막을지도 훅이 정한다:
    // 브라우저 Ctrl+V 는 막지 않아야 paste 이벤트로 내 클립보드를 받을 수 있다).
    if (vk && clip.handleKey(e.nativeEvent, down, vk)) {
      return;
    }
    if (vk) {
      // 🔴 누름을 보내지 않은 키의 뗌은 보내지 않는다 — Mac 한글 입력은 누름이 229(입력기 처리)라 건너뛰는데
      //    뗌은 실제 keyCode(R=82·K=75 …)로 온다. 그대로 보내면 원격에 **누르지도 않은 키의 뗌**이 쌓인다.
      if (!down && !pressedKeys.current.has(vk)) {
        e.preventDefault();
        return;
      }
      // 눌린 키 추적(포커스 상실/종료 시 일괄 해제용).
      if (down) {
        pressedKeys.current.add(vk);
      } else {
        pressedKeys.current.delete(vk);
      }
      session.client.sendInput({ t: 'k', vk, down });
    }
    e.preventDefault(); // 브라우저 단축키·기본동작 차단(원격에 위임)
  };

  // 포커스 상실(창 blur / 탭 숨김) 시 눌린 키 일괄 해제 → 원격 키 고착 방지(자기접속 포함).
  // 언마운트(세션 종료) 시에도 해제(채널이 아직 열려 있으면 전달됨).
  useEffect(() => {
    const release = () => {
      releaseAllKeys();
      releaseAllButtons();
      clip.reset();
    };
    // 창을 떠났다 = 내 PC 의 다른 앱에서 새로 복사했을 수 있다 → 다음 Ctrl+V 는 내 클립보드를 보낸다(§9.3.1 클립보드 주인).
    const leftWindow = () => {
      release();
      clip.localFocusLost();
    };
    const onVis = () => {
      if (document.visibilityState === 'hidden') {
        leftWindow();
      }
    };
    window.addEventListener('blur', leftWindow);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      release();
      window.removeEventListener('blur', leftWindow);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [releaseAllKeys, releaseAllButtons, clip.reset, clip.localFocusLost]);

  // IME 조합 완성 텍스트(한글·이모지 등) → {t:u,s}. 비-ASCII 만 전송(ASCII 는 VK 경로가 처리 = 중복 방지).
  const onCompositionEnd = (e: React.CompositionEvent<HTMLInputElement>) => {
    const nonAscii = [...(e.data || '')].filter((c) => c.charCodeAt(0) > 0x7f).join('');
    // 그리기가 입력을 가져간 동안 한글 모드로 친 단축키가 조합 글자로 원격에 새지 않게(§9.8.1).
    if (nonAscii && !drawing.capturesInput) {
      session.client.sendInput({ t: 'u', s: nonAscii });
    }
    if (imeRef.current) {
      imeRef.current.value = '';
    }
    imeCommit.current.compositionEnd();
  };

  // 조합 이벤트 방식이 시작되면 그 전에 붙잡아 둔 글자(Mac WebKit 경로)를 먼저 보낸다 — 순서 보존.
  const onCompositionStart = () => {
    flushIme();
    imeCommit.current.compositionStart();
  };

  // macOS WebKit 한글 입력 — 조합 이벤트 없이 `input` 으로 글자를 넣고 바꿔 끼운다. 확정된 부분만 보낸다.
  const onImeInput = (e: React.FormEvent<HTMLInputElement>) => {
    const ne = e.nativeEvent as InputEvent;
    const s = imeCommit.current.onInput(e.currentTarget.value, ne.inputType ?? '', ne.isComposing ?? false);
    if (s && !drawing.capturesInput) {
      session.client.sendInput({ t: 'u', s });
    }
  };

  // ── 파일 끌어다 놓기(§9.4) — 놓은 좌표의 원격 창(탐색기·바탕화면)에 붙여넣는다. 여백에 놓으면 원격의 현재 위치에. ──
  const onDragOver = (e: React.DragEvent) => {
    if (!files.available || !hasFiles(e.dataTransfer)) {
      return; // 파일이 아니거나 이 세션이 받을 수 없으면 브라우저 기본 동작(= 아무 일 없음)
    }
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    if (!dragging) {
      setDragging(true);
    }
  };
  const onDragLeave = (e: React.DragEvent) => {
    // 자식 요소로 옮겨 가는 것도 leave 로 온다 → 무대 밖으로 나갈 때만 끈다.
    if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node | null)) {
      setDragging(false);
    }
  };
  const onDrop = (e: React.DragEvent) => {
    setDragging(false);
    if (!files.available || !hasFiles(e.dataTransfer)) {
      return;
    }
    e.preventDefault();
    const v = videoRef.current;
    const p = v ? videoPoint(v, e.clientX, e.clientY) : null;
    // ⚠️ 이벤트 처리 중에 넘겨야 항목 목록을 읽을 수 있다.
    files.sendDataTransfer(e.dataTransfer, p ? { kind: 'drop', nx: p.nx, ny: p.ny } : { kind: 'paste' });
    imeRef.current?.focus();
  };

  // ── 통합앱 끌어다 놓기 — 네이티브 drag-drop(경로는 네이티브가 핸들로 바꾼다, files/native.ts). ──
  // 앱은 Tauri 기본 설정이라 위 HTML5 onDrop 이 오지 않는다. 한 창에 탭이 여럿이어도 **보이는 탭만** 받는다.
  useEffect(() => {
    if (!IS_SHELL || !active) {
      return;
    }
    /** 창 좌표(CSS px)가 원격 화면 무대 위인가. */
    const onStage = (x?: number, y?: number) => {
      const r = stageRef.current?.getBoundingClientRect();
      return !!r && x !== undefined && y !== undefined && x >= r.left && x < r.right && y >= r.top && y < r.bottom;
    };
    let alive = true;
    let off: (() => void) | undefined;
    void listenNativeFileDrops(
      (d) => setDragging(d.over && filesRef.current.available && onStage(d.x, d.y)),
      (d) => {
        setDragging(false);
        const v = videoRef.current;
        const inside = onStage(d.x, d.y);
        // 영상 위면 그 좌표에(놓은 곳의 원격 창·폴더), 레터박스 여백이면 원격의 현재 위치에 붙여넣는다(브라우저와 같은 규칙).
        const p = inside && v ? videoPoint(v, d.x, d.y) : null;
        filesRef.current.sendNativeDrop(d, inside ? (p ? { kind: 'drop', nx: p.nx, ny: p.ny } : { kind: 'paste' }) : null);
        imeRef.current?.focus();
      },
    ).then((un) => {
      if (alive) {
        off = un;
      } else {
        un();
      }
    });
    return () => {
      alive = false;
      off?.();
      setDragging(false);
    };
  }, [active]);

  const s = session.stats;
  /** [⋯] → [접속 정보 표시] 켬/끔 — 기본 꺼짐, 세션 화면마다 따로(가끔 보는 정보라 기억하지 않는다). */
  const [statsOn, setStatsOn] = useState(false);

  /** 단추를 누른 뒤 키 입력 포커스를 원격 화면(숨김 입력칸)으로 되돌린다 — 안 그러면 다음 키가 원격에 가지 않는다. */
  const refocus = () => imeRef.current?.focus();

  /** 채팅 열기/닫기. 별도 창이 떠 있으면 닫지 않고 **앞으로 가져온다** — 세션 창 뒤로 숨은 대화 창을 찾는 가장 쉬운 길
   *  (닫기는 대화 창의 ✕ 로 한다). */
  const toggleChat = () => {
    if (chat.open && chatWindow.isOpen) {
      chatWindow.focus();
    } else {
      chat.setOpen(!chat.open);
    }
    refocus();
  };
  /** 그리기 켜기/끄기 — 그린 것이 있으면 확인 받고 끈다. */
  const toggleDrawing = async () => {
    if (drawing.active) await drawing.requestOff();
    else drawing.setActive(true);
    refocus();
  };
  const toggleRecording = () => {
    recording.toggle();
    refocus();
  };

  // [⋯] 메뉴 항목(2026-10-12 사용자 요구 — 자주 쓰지 않는 단추는 메뉴로). 기능이 없는 상대(구버전 에이전트 · 권한 없음)면 항목도 없다.
  const moreItems: MoreItem[] = [];
  if (chat.available) {
    moreItems.push({
      key: 'chat',
      icon: '💬',
      label: '채팅',
      hint: '상대와 글로 이야기합니다',
      on: chat.open,
      badge: chat.unread,
      onSelect: toggleChat,
    });
  }
  if (canDraw) {
    moreItems.push({
      key: 'draw',
      icon: '✏️',
      label: drawing.active ? '그리기 끄기' : '화면 그리기',
      hint: drawing.active ? '그린 것이 모두 사라집니다' : '원격 화면 위에 펜·도형·글자로 표시합니다(Esc = 원격 조작으로)',
      on: drawing.active,
      onSelect: toggleDrawing,
    });
  }
  if (recording.available) {
    moreItems.push({
      key: 'record',
      icon: '●',
      label: recording.active ? `녹화 멈추기 (${recording.elapsed})` : recording.preparing ? '녹화 준비 중…' : '녹화',
      hint: recording.active
        ? '멈추고 파일로 저장합니다(영상 + 이벤트 기록)'
        : '이 세션의 화면을 내 PC 에 녹화합니다. 상대 기기에 녹화 중임이 표시됩니다.',
      on: recording.active,
      disabled: recording.preparing,
      onSelect: toggleRecording,
    });
  }
  if (files.canDragOut) {
    moreItems.push({
      key: 'dragout',
      icon: '⇱',
      label: '끌어서 내 PC로 꺼내기',
      hint: '원격 파일을 끈 채 이 창 밖으로 나가 잠시 머물면 내 PC로 받습니다. 끄면 모든 끌기가 원격 안에서만 동작합니다.',
      on: files.dragOutOn,
      keepOpen: true,
      onSelect: files.toggleDragOut,
    });
  }
  moreItems.push({
    key: 'stats',
    icon: 'ⓘ',
    label: '접속 정보 표시',
    hint: '해상도 · FPS · 비트레이트 · RTT 를 "연결됨" 옆에 보입니다',
    on: statsOn,
    keepOpen: true,
    onSelect: () => setStatsOn((v) => !v),
  });

  return (
    // `plat-win` = Windows 뷰어 — 도구 막대를 작게(2026-09-29 사용자 요구, styles `.session.plat-win`).
    <div className={[embedded ? 'session embedded' : 'session', IS_WINDOWS_VIEWER ? 'plat-win' : ''].filter(Boolean).join(' ')} ref={rootRef}>
      <div className="session-bar">
        <span className={`dot ${session.ok ? 'ok' : session.ok === false ? 'err' : ''}`} title={session.state} />
        <span className="bar-state">{session.state}</span>
        {/* 접속 정보는 원격 화면 위가 아니라 연결 상태 옆에, [⋯] → [접속 정보 표시] 를 켰을 때만(2026-10-12 사용자 요구). */}
        {statsOn && <SessionStats stats={s} />}
        {remaining !== null && (
          <span
            className="countdown"
            title="계정 없는 즉석 접속의 남은 사용 시간"
            style={{
              marginLeft: 10,
              padding: '2px 8px',
              borderRadius: 6,
              fontVariantNumeric: 'tabular-nums',
              background: remaining < 60_000 ? 'var(--err)' : 'rgba(255,255,255,0.12)',
            }}
          >
            ⏱ 남은 시간 {fmtRemaining(remaining)}
          </span>
        )}
        {/* 🔴 상대가 "원격 입력 제어 허용" 을 끈 상태. 이 표시가 없으면 화면에서는 그냥
            **마우스가 안 먹는 것**으로 보여 "멈췄다" 로 읽히고, 세션을 다시 맺는 헛수고가 생긴다.
            상태 바 왼쪽(연결 상태 바로 옆)에 둬서 입력이 안 될 때 눈이 먼저 가는 곳에 있게 한다. */}
        {session.controlAllowed === false && (
          <span
            className="countdown"
            title="상대 기기에서 원격 입력 제어를 꺼 두었습니다. 상대가 다시 켜면 즉시 조작할 수 있습니다."
            style={{
              marginLeft: 10,
              padding: '2px 8px',
              borderRadius: 6,
              background: 'var(--err)',
            }}
          >
            🔒 보기 전용 — 상대가 입력 제어를 껐습니다
          </span>
        )}
        {/* 기능 권한(§14.19.2)에 제어가 없다 — 상대가 끈 것과 **이유가 다르다**(관리자에게 요청해야 한다). */}
        {noControlGrant && session.controlAllowed !== false && (
          <span
            className="countdown"
            title="이 기기에 대한 원격 제어 권한이 없습니다. 조직 관리자에게 요청하세요."
            style={{ marginLeft: 10, padding: '2px 8px', borderRadius: 6, background: 'var(--muted)' }}
          >
            👁 보기 전용 — 제어 권한 없음
          </span>
        )}
        <span className="grow" />
        {session.monitors && session.monitors.length > 1 && (
          <span
            title="원격 모니터 전환"
            style={{ display: 'inline-flex', gap: 4, marginRight: 10, alignItems: 'center' }}
          >
            {session.monitors.map((m, idx) => {
              const active = (selMon ?? 0) === m.i;
              return (
                <button
                  key={m.i}
                  className="ghost"
                  title={`모니터 ${idx + 1} · ${m.w}×${m.h}${m.primary ? ' (주)' : ''}`}
                  onClick={() => {
                    setSelMon(m.i);
                    session.client.selectMonitor(m.i);
                    imeRef.current?.focus();
                  }}
                  style={{
                    padding: '2px 8px',
                    fontWeight: active ? 700 : 400,
                    opacity: active ? 1 : 0.55,
                  }}
                >
                  🖥 {idx + 1}
                </button>
              );
            })}
          </span>
        )}
        {/* 켜 둔 기능의 상태 표시 — 녹화는 [⋯] 안에 있으므로, 녹화 중에는 막대에 남겨 잊지 않게 한다(누르면 그 자리에서 멈춘다).
            그리기는 따로 두지 않는다 — 켜져 있는 동안 화면 위에 그리기 막대(✕ 포함)가 이미 떠 있다(DrawLayer). */}
        {(recording.active || recording.preparing) && (
          <button
            className="ghost bar-chip rec"
            disabled={recording.preparing}
            title="녹화 중 — 누르면 멈추고 파일로 저장합니다(영상 + 이벤트 기록)"
            onClick={toggleRecording}
          >
            {/* 🔴 "준비 중" 은 고지가 상대 화면에 뜨기를 기다리는 시간이다(§9.7.1) — 그 전에는 녹화가 시작되지 않는다. */}
            ● {recording.active ? recording.elapsed : '녹화 준비 중…'}
          </button>
        )}
        <TransferStatus
          transfer={files.transfer}
          notice={files.notice}
          onCancel={files.cancel}
          onDismiss={files.dismissNotice}
          refocus={refocus}
        />
        <ClipboardToast clip={clip} refocus={refocus} />
        {/* 막대에 남는 단추(2026-10-12 사용자 선택: 파일 · 전체화면) + [⋯] — 단추 사이는 좁게 붙인다(`.bar-tools`). */}
        <span className="bar-tools">
        {panel.available && (
          <button
            className={`ghost${panel.open ? ' on' : ''}`}
            title="파일 — 내 PC 와 원격의 폴더를 나란히 열어 주고받습니다(별도 창)"
            onClick={() => {
              // 창이 떠 있으면 닫지 않고 **앞으로 가져온다** — 세션 창 뒤로 숨은 파일 창을 찾는 가장 쉬운 길(채팅과 같은 규칙).
              //   (닫기는 파일 창의 [닫기]·X 로 한다.)
              if (panel.open && fileWindow.isOpen) {
                fileWindow.focus();
              } else {
                panel.setOpen(!panel.open);
              }
              imeRef.current?.focus();
            }}
          >
            🗂 <span className="bar-label">파일</span>
          </button>
        )}
        <button className="ghost" onClick={toggleFullscreen} title="전체화면">
          ⛶ <span className="bar-label">전체화면</span>
        </button>
        <SessionMoreMenu
          items={moreItems}
          quality={quality}
          clip={clip}
          badge={chat.available ? chat.unread : 0}
          refocus={refocus}
        />
        </span>
        <button
          className="ghost bar-end"
          title="연결 종료"
          onClick={async () => {
            releaseAllKeys(); // 눌린 키 해제(고착 방지) — 확인 창이 떠 있는 동안에도 원격에 키가 눌린 채 남지 않게 먼저
            // 확인 받고 진행(사용자 요청 2026-09-27).
            const ok = await confirmAction({
              title: '연결을 종료할까요?',
              message: '원격 PC와의 연결이 끊어집니다.' + (drawing.active ? ' 화면에 그린 그림도 모두 사라집니다.' : ''),
              confirmLabel: '연결 종료',
              danger: true,
            });
            if (ok) onDisconnect();
            else imeRef.current?.focus();
          }}
        >
          ⏻ <span className="bar-label">연결 종료</span>
        </button>
      </div>
      <div
        ref={stageRef}
        /* 🔴 채팅을 **옆에 고정**했을 때는 무대가 패널만큼 줄어야 한다(§9.6.6).
           패널은 `position: absolute` 라 그대로 두면 영상을 **덮는다** — 영상은 전체 폭 기준으로
           배치된 채 오른쪽이 가려져, 보이는 원격 화면이 좁아지고 남는 영역도 못 쓴다.
           여백을 주면 영상이 **남은 영역을 가득 채우도록** 다시 맞춰진다.
           별도 창(popup)일 때는 무대에 패널이 없으므로 여백을 주지 않는다. */
        className={[
          'stage',
          dragging ? 'dragging' : '',
          chat.open && chatLayout.mode === 'dock' ? 'chat-dock' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        // 고정 채팅 폭 — 무대 여백(`.stage.chat-dock`)과 패널 폭(`.cpanel.dock`)이 **같은 변수**를 읽는다(사용자가 끌어 바꾼다).
        style={{ '--chat-dock-w': `${chatLayout.dockWidth}px` } as CSSProperties}
        onClick={() => imeRef.current?.focus()}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        <video ref={videoRef} autoPlay playsInline muted />
        {/* 그리기 캔버스는 영상 바로 위 — 파일·채팅 패널보다 아래에 둬서 패널을 가리지 않는다. */}
        <DrawLayer drawing={drawing} videoRef={videoRef} stageRef={stageRef} refocus={() => imeRef.current?.focus()} />
        {dragging &&<div className="drop-hint">놓으면 원격의 그 위치에 붙여넣습니다</div>}
        {/* 키보드/IME 캡처용 숨김 입력(화면 밖). 포커스를 유지해 조합 이벤트를 받는다. */}
        <input
          ref={imeRef}
          className="ime-capture"
          onKeyDown={(e) => onKey(e, true)}
          onKeyUp={(e) => onKey(e, false)}
          onCompositionStart={onCompositionStart}
          onCompositionEnd={onCompositionEnd}
          onInput={onImeInput}
          onBlur={flushIme}
          onPaste={clip.onPaste}
          aria-hidden="true"
        />
        {/* 파일 화면은 별도 창이 그린다(`useFileWindow`). 창을 못 띄웠을 때만 **무대 안**에 대신 둔다: 무대가 위치 기준
            (position: relative)이고, 마우스 입력은 <video> 에만 걸려 있어 패널 위 클릭이 원격으로 새지 않는다(패널 뿌리에서 한 번 더 끊는다). */}
        {fileWindow.inStage && (
          <FilePanel
            panel={panel}
            refocus={() => imeRef.current?.focus()}
            banner={fileWindow.fallback ? { text: fileWindow.fallback, onClose: fileWindow.clearFallback } : undefined}
          />
        )}
        {/* 채팅 패널도 같은 이유로 무대 안이다(§9.6). 파일 패널과 동시에 열리면 나란히 눕는다. */}
        {/* 옆에 고정일 때만 무대 안에 그린다. 별도 창이면 `useChatWindow` 가 띄운 창이 그린다. */}
        {chat.open && chatLayout.mode === 'dock' && (
          <ChatPanel
            chat={chat}
            place="dock"
            onToggleMode={() => chatLayout.setMode('popup')}
            onClose={() => {
              chat.setOpen(false);
              imeRef.current?.focus();
            }}
            banner={chatWindow.fallback ? { text: chatWindow.fallback, onClose: chatWindow.clearFallback } : undefined}
            resize={{ width: chatLayout.dockWidth, onChange: chatLayout.setDockWidth }}
          />
        )}
        {/* 녹화 안내(§9.7) — 시작하지 못한 이유·자동 저장 알림. 누르면 닫힌다. */}
        {recording.notice && (
          <SaveNoticeBar notice={recording.notice} onClose={recording.clearNotice} className="rec-notice" />
        )}
        {/* 그리기 PNG 저장 결과(§9.8.4) — 녹화 안내와 같은 자리·모양. */}
        {drawing.saveNotice && !recording.notice && (
          <SaveNoticeBar notice={drawing.saveNotice} onClose={drawing.clearSaveNotice} className="rec-notice" />
        )}
      </div>
    </div>
  );
}
